import React, {useCallback, useState} from 'react';
import CreateJobsFieldsWrapper from '../../../common/case-context-container/CreateJobsFieldsWrapper';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {ISelectItem} from '../../../../../../shared/types';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import {Add, FolderOpen} from '@mui/icons-material';
import ArrTextField from '../../../../../common/arr-text-field/ArrTextField';
import ArrSingleSelectDropdown from '../../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrRadio from '../../../../../common/arr-radio/ArrRadio';
import {useCreateReviewJobGroup} from '../../../../../../hooks/ai/review/useReview';
import {ToastType} from '../../../../../../shared/constants';
import ArrToastNotification from '../../../../../common/arr-toast-notification/ArrToastNotification';

const ALLOWED_LENGTH_GROUP_NAME = 40;
const caseTitle = (
  <Typography
    className="typo-text subhead4_size assign-to-group-title"
    tabIndex={0}
    data-testid={`assign-to-group-title-optional`}
  >
    {t('ai.review.newJobDetail.assignToGroup.title')}
    <span data-testid={`assign-to-group-optional`}>
      {' '}
      {t('ai.review.newJobDetail.assignToGroup.optional')}
    </span>
  </Typography>
);

interface IAssignToGroup {
  groupList: ISelectItem[];
  handleGroupList: (groupName: string) => void;
  selectedGroup: ISelectItem | null;
  setSelectedGroup: (group: ISelectItem | null) => void;
  isCreatingGroup: boolean;
  setIsCreatingGroup: React.Dispatch<React.SetStateAction<boolean>>;
}

const RADIO_LABEL_VALUE = t('ai.review.newJobDetail.assignToGroup.noGroup');
const SPECIAL_CHAR_REGx = /[<>*;‘/()#&]/;

const AssignToGroup = ({
  groupList,
  handleGroupList,
  selectedGroup,
  setSelectedGroup,
  isCreatingGroup,
  setIsCreatingGroup,
}: IAssignToGroup) => {
  const [newGroup, setNewGroup] = useState({id: -1, label: '', name: ''});
  const [newGroupErrorDetails, setNewGroupErrorDetails] = useState({
    status: false,
    errorText: `${newGroup.name.length}/${ALLOWED_LENGTH_GROUP_NAME}`,
  });
  const [showGroupToast, setShowGroupToast] = useState<boolean>(false);
  const [groupToastStatus, setGroupToastStatus] = useState<ToastType>(
    ToastType.Success
  );
  const [groupToastMessage, setGroupToastMessage] = useState<string>('');
  const openGroupToast = useCallback((status: ToastType, message: string) => {
    setGroupToastStatus(status);
    setGroupToastMessage(message);
    setShowGroupToast(true);
  }, []);

  const {mutate: createReviewJobGroup} = useCreateReviewJobGroup();

  const isNoGroupSelected =
    (selectedGroup === null || selectedGroup?.id === '') && !isCreatingGroup;
  const handleCreateNewGroup = () => {
    setIsCreatingGroup(true);
  };

  const handleNewGroupEdit = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewGroup({id: -1, label: e.target.value, name: e.target.value});
    if (SPECIAL_CHAR_REGx.test(e.target.value)) {
      setNewGroupErrorDetails({
        status: true,
        errorText: t(
          'ai.review.newJobDetail.assignToGroup.specialCharErrorText'
        ),
      });
    } else {
      setNewGroupErrorDetails({
        status: false,
        errorText: `${e.target.value.length}/${ALLOWED_LENGTH_GROUP_NAME}`,
      });
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key.toLowerCase() !== 'enter') return;
    handleBlur();
  };
  const handleBlur = () => {
    if (newGroup.name.length === 0)
      setNewGroupErrorDetails({
        status: true,
        errorText: t(
          'ai.review.newJobDetail.assignToGroup.newGroupInputErrorText'
        ),
      });
    else if (
      groupList.some(
        (group) =>
          group.name?.toString().toLowerCase() === newGroup.name.toLowerCase()
      )
    ) {
      setNewGroupErrorDetails({
        status: true,
        errorText: t(
          'ai.review.newJobDetail.assignToGroup.groupNameAlreadyExists'
        ),
      });
    } else if (!newGroupErrorDetails.status) {
      createReviewJobGroup(
        {name: newGroup?.name},
        {
          onSuccess: () => {
            setIsCreatingGroup(false);
            handleGroupList(newGroup?.name);
            setNewGroup({id: -1, label: '', name: ''});
            openGroupToast(
              ToastType.Success,
              t('ai.review.leftPanel.groupCreatedSuccess', {
                groupName: newGroup?.name,
              })
            );
          },
          onError: () => {
            openGroupToast(
              ToastType.Error,
              t('ai.review.leftPanel.groupCreatedFailure', {groupName: name})
            );
          },
        }
      );
      setIsCreatingGroup(false);
    }
  };

  const handleKeydown = (e: React.KeyboardEvent) => {
    if (e.key.toLowerCase() === 'enter' && !!selectedGroup) {
      handleOnRadioChange();
    }
  };

  const handleOnRadioChange = () => {
    setSelectedGroup(null);
    setIsCreatingGroup(false);
    setNewGroupErrorDetails({
      status: false,
      errorText: `0/${ALLOWED_LENGTH_GROUP_NAME}`,
    });
  };

  const handleOnSelectionChange = (group: ISelectItem | null) => {
    setSelectedGroup(group);
    setNewGroupErrorDetails({
      status: false,
      errorText: `0/${ALLOWED_LENGTH_GROUP_NAME}`,
    });
  };

  return (
    <CreateJobsFieldsWrapper header={caseTitle} className="assign-to-group">
      <Box className="assign-to-group-children">
        <Box
          role="radio"
          aria-checked={isNoGroupSelected}
          tabIndex={-1}
          className={`assign-group-radio ${isNoGroupSelected && 'highlight-assign-group'}`}
          onKeyDown={handleKeydown}
          data-testid={'assign-group-radio'}
          aria-label={RADIO_LABEL_VALUE}
        >
          <ArrRadio
            name={RADIO_LABEL_VALUE}
            value={isNoGroupSelected ? RADIO_LABEL_VALUE : ''}
            onChange={() => handleOnRadioChange()}
            data={[
              {
                label: RADIO_LABEL_VALUE,
                value: RADIO_LABEL_VALUE,
              },
            ]}
            size="small"
            dataTestId={`no-group-radio`}
          />
        </Box>
        <ArrSingleSelectDropdown
          dropdownLabel={t('ai.review.newJobDetail.assignToGroup.selectGroup')}
          dropdownItems={groupList}
          dropdownItemDisplay="name"
          dropdownItemKey="id"
          selectedValues={selectedGroup}
          onSelectionChange={handleOnSelectionChange}
          searchable
          tabIndex={-1}
          dataTestId="asd-assign-to-group-select-group"
          selectRestProps={{startAdornment: <FolderOpen />}}
          inputLabelClassName={
            !isNoGroupSelected && selectedGroup ? 'display-none' : ''
          }
          inputLabelProps={{shrink: false}}
          {...(!isNoGroupSelected &&
            !!selectedGroup &&
            !isCreatingGroup && {className: 'highlight-assign-group'})}
        />
        <ArrButton
          startIcon={<Add />}
          variant="text"
          className={`create-group-btn ${isCreatingGroup && 'highlight-assign-group'}`}
          dataTestId="create-group-btn"
          onClick={handleCreateNewGroup}
        >
          <Typography
            className="typo-text label2_size highlight-text-secondary"
            data-testid={`assign-to-group-create-group`}
          >
            {t('ai.review.newJobDetail.assignToGroup.createGroup')}
          </Typography>
        </ArrButton>
        {isCreatingGroup && (
          <ArrTextField
            label={t('ai.review.newJobDetail.assignToGroup.newGroupInputLabel')}
            className="create-group-field"
            dataTestId="create-group-field"
            onChange={handleNewGroupEdit}
            onKeyDown={handleKeyDown}
            autoFocus
            onBlur={handleBlur}
            state={!newGroupErrorDetails.status ? 'enabled' : 'error'}
            showErrorIcon={true}
            aria-live="polite"
            helperText={`${newGroup.name.length} / ${ALLOWED_LENGTH_GROUP_NAME}`}
            slotProps={{
              input: {
                inputProps: {maxLength: ALLOWED_LENGTH_GROUP_NAME},
              },
              formHelperText: {
                'aria-label': `${newGroup?.name?.length} ${t('ai.review.newJobDetail.outOf40Characters')}`,
              },
            }}
            supportText={newGroupErrorDetails.errorText}
            aria-label={t(
              'ai.review.newJobDetail.assignToGroup.newGroupInputLabel'
            )}
          />
        )}
        {showGroupToast && (
          <ArrToastNotification
            message={groupToastMessage}
            status={[groupToastStatus]}
            title={
              groupToastStatus === ToastType.Success
                ? t('common.success')
                : t('common.error')
            }
            onClose={() => setShowGroupToast(false)}
          />
        )}
      </Box>
    </CreateJobsFieldsWrapper>
  );
};

export default AssignToGroup;
